import { useEffect, useRef, useState } from 'react'
import { Modality, Valence, useGetClassifierLibraryQuery } from '../../api/client'
import PageMasthead from '../../components/PageMasthead'
import Spinner from '../../components/Spinner'
import { errorMessage } from '../../util/errorMessage'
import ClassifierLibraryEmpty from './classifier/ClassifierLibraryEmpty'
import ClassifierLibraryFilters, {
    ClassifierStatusFilter
} from './classifier/ClassifierLibraryFilters'
import ClassifierLibraryTable from './classifier/ClassifierLibraryTable'
import { loadSearchState, saveSearchState } from './classifier/classifierLibrarySearchState'
import NewClassifierButton from './classifier/NewClassifierButton'

export default function ClassifierLibraryPage() {
    // Restored from the last visit, so coming back from an edit screen lands on the same list.
    const [saved] = useState(loadSearchState)
    const [offset, setOffset] = useState(saved.offset)
    const [limit, setLimit] = useState(saved.limit)
    // Raw input vs the debounced value actually sent to the server.
    const [searchInput, setSearchInput] = useState(saved.search)
    const [search, setSearch] = useState(saved.search)
    const [modality, setModality] = useState<Modality | undefined>(saved.modality)
    const [valence, setValence] = useState<Valence | undefined>(saved.valence)
    const [teamId, setTeamId] = useState<number | undefined>(saved.teamId)
    const [accountIds, setAccountIds] = useState<number[]>(saved.accountIds)
    // Active-only by default; archived classifiers are hidden unless asked for.
    const [status, setStatus] = useState<ClassifierStatusFilter>(saved.status)
    const archived = status === 'all' ? undefined : status === 'archived'

    // Debounce the search box so a query doesn't fire on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => clearTimeout(t)
    }, [searchInput])

    // Any filter change resets to the first page. Comparing against the previous key, rather
    // than running on mount, is what lets a restored page number survive. — Claude
    const filterKey = JSON.stringify([search, modality, valence, status, teamId, accountIds])
    const lastFilterKey = useRef(filterKey)
    useEffect(() => {
        if (lastFilterKey.current === filterKey) return
        lastFilterKey.current = filterKey
        setOffset(0)
    }, [filterKey])

    useEffect(() => {
        saveSearchState({ search, modality, valence, status, teamId, accountIds, offset, limit })
    }, [search, modality, valence, status, teamId, accountIds, offset, limit])

    const { data, loading, error } = useGetClassifierLibraryQuery({
        variables: {
            params: {
                search: search || undefined,
                modality: modality ?? undefined,
                valence,
                archived,
                teamId,
                accountIds: accountIds.length > 0 ? accountIds : undefined,
                offset,
                limit
            }
        },
        fetchPolicy: 'cache-and-network'
    })

    const items = data?.getClassifierLibrary?.items ?? []
    const total = data?.getClassifierLibrary?.total ?? 0
    const hasFilters =
        search !== '' ||
        modality !== undefined ||
        valence !== undefined ||
        status !== 'active' ||
        teamId !== undefined ||
        accountIds.length > 0

    function handlePageChange(newOffset: number, newLimit: number) {
        if (newLimit !== limit) {
            setLimit(newLimit)
            setOffset(0)
        } else {
            setOffset(newOffset)
        }
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title="Classifier configuration"
                tabTitle="Classifiers"
                subtitle={`${total} classifier${total !== 1 ? 's' : ''}`}
                actions={<NewClassifierButton />}
            />

            <ClassifierLibraryFilters
                search={searchInput}
                onSearchChange={setSearchInput}
                modality={modality}
                onModalityChange={setModality}
                valence={valence}
                onValenceChange={setValence}
                status={status}
                onStatusChange={setStatus}
                teamId={teamId}
                onTeamChange={setTeamId}
                accountIds={accountIds}
                onAccountIdsChange={setAccountIds}
            />

            <div className="p-6">
                {error ? (
                    <div className="rounded-lg border border-severe/30 bg-severe/10 p-4 text-sm text-severe">
                        Couldn&rsquo;t load classifiers: {errorMessage(error)}
                    </div>
                ) : loading && items.length === 0 ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size={6} />
                    </div>
                ) : items.length === 0 ? (
                    <ClassifierLibraryEmpty filtered={hasFilters} />
                ) : (
                    <ClassifierLibraryTable
                        items={items}
                        offset={offset}
                        limit={limit}
                        total={total}
                        loading={loading}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    )
}
